import { useState, useCallback } from 'react';
import { Check, FlaskConical, Scale, Pill } from 'lucide-react';
import { useExperiment } from '@/context';
import { AnalyticalBalance } from '@/components/lab';
import MortarAndPestle from '@/components/lab/MortarAndPestle';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Weighing the Tablets',
    content: 'Accurately weighing multiple tablets together allows us to determine the average mass per tablet. This is important because tablet masses can vary slightly, and knowing the average helps us calculate the vitamin C content per tablet from our sample measurements.',
    whatHappens: 'Ten tablets are placed on the analytical balance. The total mass is recorded for calculating the average tablet mass.',
    observe: [
      'Balance reading stabilizes',
      'Total mass should be 5-6g for 10 tablets',
      'Record the precise mass',
      'Tablets should be counted accurately'
    ]
  },
  2: {
    title: 'Calculating Average Mass',
    content: 'The average tablet mass is crucial for the final calculation. Since we analyze powdered samples (not whole tablets), we need to convert our results back to "per tablet" values using the average mass.',
    whatHappens: 'Divide the total mass by 10 to get the average mass per tablet.',
    observe: [
      'Average = Total mass / 10',
      'Typical vitamin C tablet: 0.5-0.6g',
      'Record to 4 decimal places',
      'This value is used in calculations'
    ]
  },
  3: {
    title: 'Grinding Tablets',
    content: 'With a mortar and pestle, grind up 3-5 tablets. Grinding ensures uniform distribution of vitamin C throughout the sample. Fine powder dissolves more readily.',
    whatHappens: '3-5 tablets are set aside for grinding. The mortar and pestle creates a fine, homogeneous powder.',
    observe: [
      'Select 3-5 intact tablets',
      'Tablets should be similar in appearance',
      'Avoid broken or damaged tablets',
      'These will be ground together'
    ]
  },
  4: {
    title: 'Grinding Tablets',
    content: 'Grinding creates a homogeneous powder, ensuring that any sample taken is representative of the whole. Ascorbic acid (vitamin C) is distributed evenly throughout the powder, making weighing small amounts reliable.',
    whatHappens: 'The mortar and pestle mechanically break down the tablets into a fine, uniform powder.',
    observe: [
      'Grind until powder is fine',
      'No large chunks should remain',
      'Powder should be uniform color',
      'Transfer carefully to avoid loss'
    ]
  },
  5: {
    title: 'Sample Weighing',
    content: 'Accurately weigh four samples of about 400 mg each. Record the exact weight of each sample. Quantitatively transfer the samples to four pre-labelled Erlenmeyer flasks as rapid titration, sample 1, 2, and 3.',
    whatHappens: 'A precise amount of powder (~400 mg) is weighed on the analytical balance. The exact mass is recorded for calculations.',
    observe: [
      'Target mass: ~400 mg (0.4 g)',
      'Record exact mass to 0.0001 g',
      'Use weighing paper or boat',
      'Transfer quantitatively to labelled flask'
    ]
  },
  6: {
    title: 'Weighing Sample 2',
    content: 'The second replicate sample provides additional data for statistical analysis. Comparing results from multiple samples helps identify measurement errors and improves confidence in the final result.',
    whatHappens: 'Another ~400mg sample is weighed precisely.',
    observe: [
      'Same procedure as Sample 1',
      'Mass may differ slightly',
      'Record the exact mass',
      'Consistency in technique is key'
    ]
  },
  7: {
    title: 'Weighing Sample 3',
    content: 'With three samples, we can calculate a mean and identify if any one result is significantly different (an outlier). Statistical treatment of replicate data is a fundamental principle of quantitative analysis.',
    whatHappens: 'Third sample is weighed with the same precision.',
    observe: [
      'Continue careful technique',
      'Keep powder dry',
      'Avoid contamination',
      'Record all masses'
    ]
  },
  8: {
    title: 'Weighing Sample 4',
    content: 'The fourth replicate provides additional statistical power. With four samples, we can calculate standard deviation and relative standard deviation (%RSD) to assess the precision of our analysis method.',
    whatHappens: 'Final sample is weighed to complete the set of four replicates.',
    observe: [
      'Final sample preparation',
      'All four samples ready for titration',
      'Masses should be similar',
      'Proceed to titration step'
    ]
  }
};

interface Sample {
  id: number;
  mass: number | null;
  prepared: boolean;
}

export default function Part1SamplePrep() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const partState = state?.experimentData?.samplePrep || {
    tabletsWeighed: 0,
    totalMass: null,
    avgTabletMass: null,
    tabletsGround: 0,
    grindingProgress: 0,
    samples: [
      { id: 1, mass: null, prepared: false },
      { id: 2, mass: null, prepared: false },
      { id: 3, mass: null, prepared: false },
      { id: 4, mass: null, prepared: false }
    ] as Sample[]
  };

  const [weighing, setWeighing] = useState(false);
  const [grinding, setGrinding] = useState(false);
  const [currentSample, setCurrentSample] = useState(1);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('samplePrep', { ...partState, ...updates });
  }, [partState, updatePartData]);

  const handleWeighTablets = useCallback(() => {
    setWeighing(true);
    // Simulate weighing 10 tablets (typical mass 5-6g total)
    setTimeout(() => {
      const totalMass = 5.2 + Math.random() * 0.8; // 5.2-6.0g
      updateState({
        tabletsWeighed: 10,
        totalMass: parseFloat(totalMass.toFixed(4))
      });
      setWeighing(false);
      completeStep('samplePrep-1');
    }, 2000);
  }, [updateState, completeStep]);

  const handleCalculateAverage = useCallback(() => {
    if (partState.totalMass) {
      const avgMass = partState.totalMass / 10;
      updateState({ avgTabletMass: parseFloat(avgMass.toFixed(4)) });
      completeStep('samplePrep-2');
    }
  }, [partState.totalMass, updateState, completeStep]);

  const handleSelectTablets = useCallback(() => {
    updateState({ tabletsGround: 0 });
    completeStep('samplePrep-3');
  }, [updateState, completeStep]);

  const handleGrindTablets = useCallback(() => {
    setGrinding(true);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      updateState({ grindingProgress: progress });
      if (progress >= 100) {
        clearInterval(interval);
        setGrinding(false);
        updateState({ tabletsGround: 4, grindingProgress: 100 });
        completeStep('samplePrep-4');
      }
    }, 300);
  }, [updateState, completeStep]);

  const handleWeighSample = useCallback((sampleId: number) => {
    if (partState.tabletsGround < 4) return;

    setWeighing(true);
    setTimeout(() => {
      // Sample mass around 400mg (0.4g) with some variation
      const sampleMass = 0.38 + Math.random() * 0.04;
      const newSamples = [...partState.samples];
      newSamples[sampleId - 1] = {
        ...newSamples[sampleId - 1],
        mass: parseFloat(sampleMass.toFixed(4)),
        prepared: true
      };
      updateState({ samples: newSamples });
      setWeighing(false);

      // Complete the corresponding step (5-8)
      const stepId = 4 + sampleId;
      completeStep(`samplePrep-${stepId}`);

      if (sampleId < 4) {
        setCurrentSample(sampleId + 1);
      }
    }, 1500);
  }, [partState.tabletsGround, partState.samples, updateState, completeStep]);

  const allSamplesPrepared = partState.samples.every((s: Sample) => s.prepared);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part I: Sample Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Count and Weigh Tablets', hint: 'Weigh 10 vitamin C tablets together' },
            { id: 2, title: 'Calculate Average Mass', hint: 'Divide total mass by 10' },
            { id: 3, title: 'Grind 3-5 Tablets', hint: 'Select 3-5 tablets for grinding' },
            { id: 4, title: 'Grind Tablets', hint: 'Use mortar and pestle to create fine powder' },
            { id: 5, title: 'Weigh Sample 1', hint: 'Weigh approximately 400 mg of powder' },
            { id: 6, title: 'Weigh Sample 2', hint: 'Weigh approximately 400 mg of powder' },
            { id: 7, title: 'Weigh Sample 3', hint: 'Weigh approximately 400 mg of powder' },
            { id: 8, title: 'Weigh Sample 4', hint: 'Weigh approximately 400 mg of powder' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`samplePrep-${step.id}`) ? 'completed' : ''} ${
                state?.currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`samplePrep-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`samplePrep-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {state?.currentStep === step.id && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Sample Data Table */}
        <div className="data-table">
          <h4>Sample Masses</h4>
          <table>
            <thead>
              <tr>
                <th>Sample</th>
                <th>Mass (g)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {partState.samples.map((sample: Sample) => (
                <tr key={sample.id} className={sample.id === currentSample ? 'current' : ''}>
                  <td>Sample {sample.id}</td>
                  <td>{sample.mass ? sample.mass.toFixed(4) : '-'}</td>
                  <td>{sample.prepared ? <Check size={14} color="#00A86B" /> : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <LabBench
        experimentName="Vitamin C Sample Preparation"
        completedCount={state?.completedSteps.filter(s => s.startsWith('samplePrep-')).length || 0}
        totalSteps={8}
        equipment={['Analytical balance', 'Mortar and pestle']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="sample-prep-workspace">
          <div className="equipment-row">
            {/* Tablets visualization */}
            <div className="tablets-station">
              <div className="tablets-container">
                {[...Array(10)].map((_, i) => (
                  <Pill
                    key={i}
                    size={24}
                    className={`tablet ${i < partState.tabletsWeighed ? 'weighed' : ''} ${
                      i >= 6 && partState.tabletsGround > 0 ? 'ground' : ''
                    }`}
                    style={{
                      color: i >= 6 && partState.tabletsGround > 0 ? '#D4D4D4' : '#FFA500',
                      opacity: i >= 6 && partState.grindingProgress > 0 ? 0.3 : 1
                    }}
                  />
                ))}
              </div>
              <span className="equipment-label">Vitamin C Tablets (10)</span>
            </div>

            {/* Analytical Balance */}
            <div className="equipment-station">
              <AnalyticalBalance
                reading={
                  weighing ? 0 :
                  partState.samples[currentSample - 1]?.mass ||
                  partState.totalMass ||
                  0
                }
                unit="g"
                stable={!weighing}
                onClick={() => {
                  if (!isStepCompleted('samplePrep-1')) {
                    handleWeighTablets();
                  } else if (partState.tabletsGround >= 4 && currentSample <= 4) {
                    const stepId = 4 + currentSample;
                    if (!isStepCompleted(`samplePrep-${stepId}`)) {
                      handleWeighSample(currentSample);
                    }
                  }
                }}
              />
              <span className="equipment-label">Analytical Balance</span>
            </div>

            {/* Mortar and Pestle */}
            <div className="equipment-station">
              <MortarAndPestle
                hasContent={isStepCompleted('samplePrep-3')}
                grinding={grinding}
                contentColor="#FFA500"
                contentLevel={100 - partState.grindingProgress * 0.3}
                onClick={() => {
                  if (isStepCompleted('samplePrep-3') && !isStepCompleted('samplePrep-4')) {
                    handleGrindTablets();
                  }
                }}
              />
              <span className="equipment-label">Mortar & Pestle</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {isStepCompleted('samplePrep-1') && !isStepCompleted('samplePrep-2') && (
              <button className="action-btn primary" onClick={handleCalculateAverage}>
                <Scale size={18} />
                Calculate Average: {partState.totalMass ? (partState.totalMass / 10).toFixed(4) : 0} g
              </button>
            )}

            {isStepCompleted('samplePrep-2') && !isStepCompleted('samplePrep-3') && (
              <button className="action-btn primary" onClick={handleSelectTablets}>
                <Pill size={18} />
                Select 3-5 Tablets for Grinding
              </button>
            )}

            {grinding && (
              <div className="grinding-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${partState.grindingProgress}%` }}
                  />
                </div>
                <span>Grinding: {partState.grindingProgress}%</span>
              </div>
            )}
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {weighing && (
              <div className="status-message">
                <Scale size={20} className="spin" />
                Weighing...
              </div>
            )}

            {partState.avgTabletMass && !isStepCompleted('samplePrep-4') && (
              <div className="info-box">
                Average tablet mass: <strong>{partState.avgTabletMass.toFixed(4)} g</strong>
              </div>
            )}

            {allSamplesPrepared && (
              <div className="status-message success">
                <Check size={20} />
                All samples prepared! Ready for titration.
              </div>
            )}
          </div>
        </div>
      </LabBench>

      <style>{`
        .sample-prep-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 30px;
          padding: 20px;
          width: 100%;
        }

        .equipment-row {
          display: flex;
          gap: 50px;
          align-items: flex-end;
          justify-content: center;
        }

        .equipment-station, .tablets-station {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .tablets-container {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 8px;
          padding: 16px;
          background: var(--bg-elevated);
          border-radius: 12px;
          border: 1px solid var(--border);
        }

        .tablet {
          transition: all 0.3s;
        }

        .tablet.weighed {
          filter: brightness(1.2);
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
          text-align: center;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: var(--primary);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .grinding-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          width: 200px;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: var(--bg-dark);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: var(--primary);
          transition: width 0.2s;
        }

        .status-area {
          min-height: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .status-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          background: rgba(255, 165, 0, 0.2);
          color: #FFA500;
        }

        .status-message.success {
          background: rgba(0, 168, 107, 0.2);
          color: var(--secondary);
        }

        .info-box {
          padding: 12px 20px;
          background: var(--bg-elevated);
          border-radius: 8px;
          font-size: 14px;
          color: var(--text-primary);
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .data-table {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .data-table h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .data-table table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .data-table th, .data-table td {
          padding: 6px 8px;
          text-align: center;
          border-bottom: 1px solid var(--border);
        }

        .data-table th {
          color: var(--text-muted);
          font-weight: 600;
        }

        .data-table tr.current {
          background: rgba(255, 90, 54, 0.1);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
